// Vacate onto shelves the host already occupies so x (CDT order) stays put.
import { GeneratedLayout, StudioItem, StudioRunGeometry } from "../studioTypes";
import { laneSegmentsOf, openingsOf } from "./layoutOps";

export interface VacateRequest {
  orphanKey: string;
  /** Shelf ranks the orphan's column needs freed of the host. */
  neededShelves: readonly number[];
  blockKeyOf: (skuId: string) => string;
  run: StudioRunGeometry;
}

/** Mutates `layout`. Returns the host keys that moved, or null if none can vacate. */
export const vacateShelf = (
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  request: VacateRequest,
): string[] | null => {
  const { orphanKey, neededShelves, blockKeyOf, run } = request;
  const heights = new Map(items.map((item) => [item.skuId, item.heightCm || 0]));
  const openings = openingsOf(run);
  const lanes = laneSegmentsOf(layout, items);
  const moved: string[] = [];

  for (const shelf of neededShelves) {
    const lane = (lanes.get(`${run.run}|${shelf}`) ?? []).filter(
      (entry) => blockKeyOf(entry.skuId) !== orphanKey,
    );
    // Hosts must come from the contested shelf, nearest the orphan first;
    // ranking by mean x over the whole run picks the wrong block.
    const orphanLane = (lanes.get(`${run.run}|${shelf}`) ?? []).filter(
      (entry) => blockKeyOf(entry.skuId) === orphanKey,
    );
    const orphanCenter =
      orphanLane.length > 0
        ? orphanLane.reduce((sum, entry) => sum + entry.segment.xCm, 0) / orphanLane.length
        : 0;
    const hosts = [...new Set(lane.map((entry) => blockKeyOf(entry.skuId)))].sort((a, b) => {
      const center = (key: string) => {
        const own = lane.filter((entry) => blockKeyOf(entry.skuId) === key);
        return own.reduce((sum, entry) => sum + entry.segment.xCm, 0) / Math.max(1, own.length);
      };
      return Math.abs(center(a) - orphanCenter) - Math.abs(center(b) - orphanCenter);
    });

    let vacated = false;
    for (const host of hosts) {
      const hostSegments = lane.filter((entry) => blockKeyOf(entry.skuId) === host);
      // Shelves the host already occupies elsewhere in this run.
      const elsewhere = new Set<number>();
      for (const [laneKey, laneEntries] of lanes) {
        const [runId, rank] = laneKey.split("|").map(Number);
        if (runId !== run.run || rank === shelf) continue;
        if (laneEntries.some((entry) => blockKeyOf(entry.skuId) === host)) elsewhere.add(rank);
      }
      if (elsewhere.size === 0) continue;
      const fits = hostSegments.every((entry) =>
        [...elsewhere].some(
          (rank) => (openings.get(rank) ?? Infinity) + 0.5 >= (heights.get(entry.skuId) ?? 0),
        ),
      );
      if (!fits) continue;
      for (const entry of hostSegments) {
        const target = [...elsewhere]
          .filter(
            (rank) => (openings.get(rank) ?? Infinity) + 0.5 >= (heights.get(entry.skuId) ?? 0),
          )
          .sort((a, b) => Math.abs(a - shelf) - Math.abs(b - shelf))[0];
        entry.segment.shelf = target;
      }
      moved.push(host);
      vacated = true;
      break;
    }
    if (!vacated) return null;
  }
  return moved;
};
